import { useEffect, useState } from "react";
import { formatMoneyDigits } from "../utils/formatMoney";
import { parseAmount } from "../utils/parseAmount";

// Input de monto en CRC. Mantiene dos representaciones:
//   - canonical: string decimal "2500000.00" — lo que se envía al backend
//                y lo que el padre observa (vía onChange).
//   - display:   lo que el usuario ve. Mientras edita = lo que escribió.
//                On blur = formateo "₡2.500.000".
//
// Por qué <input type="text">: <input type="number"> tiene UX rota (spinner,
// scroll cambia valor, locale). Texto + parse tolerante = más simple.

type Props = {
  value: string; // canonical decimal string ("" cuando no hay valor)
  onChange: (canonical: string) => void;
  disabled?: boolean;
  id?: string;
  placeholder?: string;
  "aria-label"?: string;
  "aria-invalid"?: boolean;
};

export function AmountInput({
  value,
  onChange,
  disabled,
  id,
  placeholder = "0",
  "aria-label": ariaLabel,
  "aria-invalid": ariaInvalid,
}: Props) {
  const [display, setDisplay] = useState(() => (value ? formatMoneyDigits(value) : ""));
  const [focused, setFocused] = useState(false);

  // Si el valor canónico cambia desde afuera (reset del form), reflejarlo.
  useEffect(() => {
    if (focused) return;
    setDisplay(value ? formatMoneyDigits(value) : "");
  }, [value, focused]);

  return (
    <div className="relative">
      <span
        className="pointer-events-none absolute inset-y-0 left-3 flex items-center font-medium text-[15px] text-app-ink-2"
        aria-hidden="true"
      >
        ₡
      </span>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        value={display}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        onFocus={() => {
          setFocused(true);
          // Mostrar valor crudo (más fácil de editar) al enfocar.
          if (value) setDisplay(value.replace(/\.?0+$/, ""));
        }}
        onChange={(e) => setDisplay(e.target.value)}
        onBlur={() => {
          setFocused(false);
          const canonical = parseAmount(display);
          if (canonical === null) {
            // Si quedó vacío o inválido, limpiamos.
            setDisplay("");
            onChange("");
          } else {
            onChange(canonical);
            setDisplay(formatMoneyDigits(canonical));
          }
        }}
        className="h-10 w-full rounded-lg border border-app-border bg-app-surface pr-3 pl-7 text-app-ink-1 text-base outline-none focus-visible:border-app-emerald focus-visible:ring-3 focus-visible:ring-app-emerald/20 disabled:opacity-50"
      />
    </div>
  );
}
